import React from 'react'
import { useTranslation } from 'react-i18next'

import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import TextField from '@mui/material/TextField'
import DialogActions from '@mui/material/DialogActions'
import LoadingButton from '@mui/lab/LoadingButton'

import Alert from '../alert/alert'
import { Log } from '../../types/log'
import { patch } from '../../utils/service'
import { getFullscreenElement } from '../../utils/graphUtils'

interface LogChartTitleProps {
  title: string
  logUrl: string
  onClose: () => void
  onTitleChanged: (updatedLogData: Log) => void
}

const LogChartTitle = (props: LogChartTitleProps): JSX.Element => {
  const { t } = useTranslation()

  const [chartTitle, setChartTitle] = React.useState<string>(props.title)
  const [isSavingChartTitle, setIsSavingChartTitle] = React.useState<boolean>(false)
  const [hasChartTitleError, setHasChartTitleError] = React.useState<boolean>(false)

  React.useEffect(() => {
    setChartTitle(props.title)
  }, [props.title])

  const onTitleChange = (newTitle: string): void => {
    if (props.logUrl) {
      setIsSavingChartTitle(true)
      setHasChartTitleError(false)
      // PATCH to server
      patch<{ name: string | null }, Log>(props.logUrl, { name: newTitle || null })
        .then((patchLogData) => {
          setIsSavingChartTitle(false)
          props.onTitleChanged(patchLogData.data)
        })
        .catch(() => {
          setIsSavingChartTitle(false)
          setHasChartTitleError(true)
        })
    }
  }

  const updateChartTitle = (): void => {
    onTitleChange(chartTitle)
  }

  return (
    <Dialog open={true} onClose={props.onClose} container={getFullscreenElement() || document.body}>
      <DialogContent>
        <TextField
          autoFocus
          label={t('logview.logname')}
          value={chartTitle}
          onChange={(event) => setChartTitle(event.target.value)}
          inputProps={{ maxLength: 200 }}
          onKeyPress={(e): void => {
            if (e.key === 'Enter') {
              updateChartTitle()
            }
          }}
        />
        {hasChartTitleError && <Alert severity="error">{t('logview.updatenameerror')}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button disabled={isSavingChartTitle} onClick={props.onClose}>
          {t('common.cancel')}
        </Button>
        <LoadingButton loading={isSavingChartTitle} variant="contained" onClick={updateChartTitle}>
          {t('logview.updatename')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

export default LogChartTitle
